/**
 * A DATE AND TIME FIELD IN A NAMED TIME ZONE (design 24, T29) - the cabinet's "Publication date and
 * time", built on `@mantine/dates`' `DateTimePicker`, which this package already carries, so the
 * apps add no dependency.
 *
 * ★ THE VALUE IS AN INSTANT; THE PICKER SHOWS A WALL TIME IN `timeZone`. `value` and `onChange`
 * speak `YYYY-MM-DDTHH:mm:ssZ`, the wire's instant, and the picker shows and takes the time as a
 * clock in that zone reads it (./zoned-time.ts says why the picker's own parsing is not used). The
 * zone is said in the field's description, so the author knows whose clock it is.
 *
 * ★ NO CLOCK IS READ HERE (C11). The first day that can be picked (`minDay`) and the day the
 * calendar opens on when the field is empty (`openingDay`) are handed in; the cabinet takes both
 * from a server response. And the time cannot be set before a day is: the picker would put a time
 * typed into an empty field on the BROWSER's today.
 *
 * ★ QUICK PICKS. Days offered under the field (`quickPicks`); a click fills the date and keeps the
 * time already set, or `defaultTime` when the field is empty. A day before `minDay` is not offered.
 *
 * ★ THE ONLY WORDS OF ITS OWN ARE FOR ASSISTIVE TECHNOLOGY: names for the calendar's arrows and
 * level buttons, the time's two boxes, and the clear and done buttons, which Mantine leaves unnamed.
 */
import { Button, Group, Stack, Text } from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import type { ReactNode } from 'react';
import { useUiText } from '../../texts/ui-texts';
import type { UiTranslate } from '../../texts/ui-texts';
import { instantToWall, isKnownTimeZone, wallToInstant } from './zoned-time';

/** One day offered under the field. */
export interface DateTimeFieldQuickPick {
  /** `YYYY-MM-DD`, a day in the field's zone. */
  readonly day: string;
  /** What the button says, e.g. "14 Oct 2026 — Full moon in Aries". */
  readonly label: string;
}

export interface DateTimeFieldProps {
  readonly label: string;
  /** The field's line; the zone is said after it. */
  readonly description?: ReactNode;
  /** The instant, `YYYY-MM-DDTHH:mm:ssZ` (any ISO 8601 instant is read), or `null` for none. */
  readonly value: string | null;
  /** The instant picked, to the second, or `null` once cleared. */
  readonly onChange: (instant: string | null) => void;
  /** The IANA zone the time is in, e.g. `Europe/Minsk`. An unknown one falls back to UTC. */
  readonly timeZone: string;
  /** `YYYY-MM-DD`: no day before it can be picked. */
  readonly minDay?: string | undefined;
  /** `YYYY-MM-DD`: the month the calendar opens on while the field is empty. */
  readonly openingDay?: string | undefined;
  readonly disabled?: boolean | undefined;
  readonly placeholder?: string | undefined;
  readonly quickPicks?: readonly DateTimeFieldQuickPick[] | undefined;
  /** The words before the quick picks. */
  readonly quickPicksLabel?: string | undefined;
  /** `HH:mm`: the time a quick pick or a picked day takes while the field is empty. */
  readonly defaultTime?: string | undefined;
}

/** The calendar's controls, named: Mantine draws them as bare buttons. */
function calendarLabels(t: UiTranslate) {
  return {
    previousMonth: t('calendar.previousMonth'),
    nextMonth: t('calendar.nextMonth'),
    previousYear: t('calendar.previousYear'),
    nextYear: t('calendar.nextYear'),
    previousDecade: t('calendar.previousDecade'),
    nextDecade: t('calendar.nextDecade'),
    monthLevelControl: t('calendar.chooseAnotherMonth'),
    yearLevelControl: t('calendar.chooseAnotherYear'),
  };
}

export function DateTimeField({
  label,
  description,
  value,
  onChange,
  timeZone,
  minDay,
  openingDay,
  disabled = false,
  placeholder,
  quickPicks = [],
  quickPicksLabel,
  defaultTime = '09:00',
}: DateTimeFieldProps) {
  const t = useUiText();
  const zone = isKnownTimeZone(timeZone) ? timeZone : 'UTC';
  const wall = value === null ? null : instantToWall(value, zone);
  const picks = quickPicks.filter((pick) => minDay === undefined || pick.day >= minDay);

  const send = (nextWall: string | null) => {
    onChange(nextWall === null ? null : wallToInstant(nextWall, zone));
  };

  return (
    <Stack gap={6}>
      <DateTimePicker
        label={label}
        description={
          <>
            {description}
            {description === undefined ? null : <br />}
            {t('dateTimeField.timesAreIn', { zone })}
          </>
        }
        {...(placeholder === undefined ? {} : { placeholder })}
        // The picker draws its placeholder as real text (a button, not an <input>), so axe measures
        // it: Mantine's placeholder grey is 3.42:1 on the dark surface. The dimmed ink is the one
        // muted text this theme has proved on every surface (the accessibility leg, pause 14).
        styles={{ placeholder: { color: 'var(--mantine-color-dimmed)' } }}
        disabled={disabled}
        clearable
        clearButtonProps={{ 'aria-label': t('dateTimeField.clear') }}
        value={wall}
        onChange={send}
        valueFormat="D MMM YYYY, HH:mm"
        {...(minDay === undefined ? {} : { minDate: minDay })}
        {...(openingDay === undefined ? {} : { defaultDate: openingDay })}
        // An empty field shows the default time, and takes it with the day picked (the header).
        {...(wall === null ? { defaultTimeValue: defaultTime } : {})}
        timePickerProps={{
          hoursInputLabel: t('dateTimeField.hours'),
          minutesInputLabel: t('dateTimeField.minutes'),
          disabled: wall === null,
        }}
        submitButtonProps={{ 'aria-label': t('dateTimeField.done') }}
        ariaLabels={calendarLabels(t)}
      />
      {picks.length === 0 ? null : (
        <Group gap={6} wrap="wrap" align="center">
          <Text size="xs" c="dimmed" component="span">
            {quickPicksLabel ?? t('dateTimeField.suggested')}
          </Text>
          {picks.map((pick) => (
            <Button
              key={pick.day}
              size="compact-xs"
              variant="light"
              radius="xl"
              disabled={disabled}
              onClick={() => {
                // The day from the pick, the time kept - or the default on an empty field.
                send(`${pick.day} ${wall === null ? `${defaultTime}:00` : wall.slice(11)}`);
              }}
            >
              {pick.label}
            </Button>
          ))}
        </Group>
      )}
    </Stack>
  );
}
